import axios from 'axios';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

// ============= New Workflow Types =============

export interface CreateTenantRequestDto {
  name: string;
  email: string;
  phone?: string;
  suggestedTenantName: string;
  note?: string;
}

export interface CompleteInviteDto {
  token: string;
  password: string;
}

export interface BootstrapActivationPayload {
  name: string;
  email: string;
  password: string;
  verificationCode: string;
  tenantName?: string;
}

// ============= New Workflow Endpoints =============

export const createTenantRequest = async (data: CreateTenantRequestDto) => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/tenant-requests`, data);
  return response.data;
};

export const getTenantRequests = async () => {
  const response = await axios.get(`${API_URL}/admin/tenant-requests`);
  return response.data;
};

export const getTenantRequestsPendingCount = async (): Promise<{ count: number }> => {
  const response = await axios.get(`${API_URL}/admin/tenant-requests/pending-count`);
  return response.data;
};

export const approveTenantRequest = async (
  requestId: number,
  action: 'associate' | 'create',
  payload: {
    tenantId?: number;
    name?: string;
    logoUrl?: string;
    emailFatturazione?: string;
    companyAddress?: string;
    vatNumber?: string;
    pec?: string;
    primaryColor?: string;
    secondaryColor?: string;
    language?: 'en' | 'it';
    autoCreateCommercialEntity?: boolean;
    commercialEntityType?: 'CLIENT' | 'SUPPLIER' | 'BOTH';
    role?: string;
  } = {}
) => {
  if (action === 'create') {
    const response = await axios.post(
      `${API_URL}/super-admin/tenant-requests/${requestId}/approve-create-tenant`,
      {
        tenantPayload: {
          name: payload.name,
          logoUrl: payload.logoUrl,
          emailFatturazione: payload.emailFatturazione,
          companyAddress: payload.companyAddress,
          vatNumber: payload.vatNumber,
          pec: payload.pec,
          primaryColor: payload.primaryColor ?? '#f97316',
          secondaryColor: payload.secondaryColor ?? '#ea580c',
          language: payload.language ?? 'it',
          autoCreateCommercialEntity: payload.autoCreateCommercialEntity,
          commercialEntityType: payload.commercialEntityType,
        },
        role: payload.role,
      }
    );
    return response.data;
  }

  const response = await axios.post(`${API_URL}/admin/tenant-requests/${requestId}/approve`, {
    action,
    tenantId: payload.tenantId,
    role: payload.role,
  });
  return response.data;
};

export const rejectTenantRequest = async (requestId: number, reason: string) => {
  const response = await axios.post(`${API_URL}/admin/tenant-requests/${requestId}/reject`, {
    reason,
  });
  return response.data;
};

export const completeInvite = async (token: string, password: string) => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/invite/complete`, {
    token,
    password,
  });
  return response.data;
};

export const getBootstrapStatus = async (): Promise<{ requiresActivation: boolean }> => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.get(`${API_URL}/auth/bootstrap/status`);
  return response.data;
};

export const sendBootstrapActivationCode = async (email: string): Promise<{ success: boolean; expiresInMinutes: number; debugCode?: string }> => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/bootstrap/send-code`, { email });
  return response.data;
};

export const completeBootstrapActivation = async (payload: BootstrapActivationPayload) => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/bootstrap/activate`, payload);
  return response.data;
};

export const getInvitePreview = async (token: string) => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.get(`${API_URL}/auth/invite/preview`, {
    params: { token },
  });
  return response.data as { email: string; name: string };
};

export const requestPasswordReset = async (email: string): Promise<{ message: string }> => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/password/forgot`, { email });
  return response.data;
};

export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/password/reset`, { token, newPassword });
  return response.data;
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  const response = await axios.post(`${API_URL}/auth/password/change`, { currentPassword, newPassword });
  return response.data;
};

// ============= Magic link (monthly timesheet reminder) =============

export interface MagicLinkPreview {
  name: string;
  email: string;
  tenantName: string | null;
  /** Tenant language: the landing page switches to it before any session exists */
  language: string | null;
  periodYear: number;
  periodMonth: number;
  expiresAt: string;
}

/** Read-only check of the link: shows who is about to log in, does not create a session. */
export const getMagicLinkPreview = async (token: string): Promise<MagicLinkPreview> => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/magic/preview`, { token });
  return response.data;
};

/** Exchanges the link for a session: same payload as /auth/login, so applyAuthPayload() takes it as is. */
export const exchangeMagicLink = async (token: string) => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.post(`${API_URL}/auth/magic/exchange`, { token });
  return response.data;
};
